import { api, depositInput, type DepositInput, queryKeys } from '@alpha/api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useTableQuery } from '../../hooks/useTableQuery';
import { formatDateTime, formatLds, formatUsd, statusTone, TOKEN_LABEL } from '../../lib/format';
import { Alert, Button, Card, Chip, FormGroup, Input } from '../../ui';
import type { Deposit as DepositRecord } from '@alpha/api-client';

export const WALLET_TABS = [
  { label: 'Deposit', to: '/wallet/deposit' },
  { label: 'Withdrawal', to: '/wallet/withdrawal' },
  { label: 'Transfer', to: '/wallet/transfer' },
  { label: 'Buy LDS1', to: '/wallet/buy-lds1' },
  { label: 'Transactions', to: '/wallet/transactions' },
];

const col = createColumnHelper<DepositRecord>();

/**
 * 2026-09-11, LDS1 subdomain build: mirrors the backend's DEPOSIT_MAX_USD
 * (config/services.php `deposit_max_usd`) purely for a frontend hint/max
 * attribute — the server enforces the real cap regardless of this value.
 */
const DEPOSIT_MAX_USD = Number((import.meta.env.VITE_DEPOSIT_MAX_USD as string | undefined) || 0);

export function Deposit() {
  const queryClient = useQueryClient();
  const { query, setPage } = useTableQuery();

  const details = useQuery({
    queryKey: ['payment-details'],
    queryFn: () => api.deposits.paymentDetails().then((r) => r.data),
  });

  const history = useQuery({
    queryKey: queryKeys.deposits(query),
    queryFn: () => api.deposits.list(query),
  });

  const form = useForm<DepositInput>({
    resolver: zodResolver(depositInput),
    defaultValues: { amount_usd: 0, method: 'blockchain', tx_hash: '' },
  });

  const submit = useMutation({
    mutationFn: (values: DepositInput) => api.deposits.create(values),
    onSuccess: () => {
      form.reset();
      queryClient.invalidateQueries({ queryKey: ['deposits'] });
    },
  });

  const columns = useMemo(
    () => [
      col.accessor('amount_usd', { header: 'USD', cell: (i) => formatUsd(i.getValue()) }),
      col.accessor('amount_lds', { header: TOKEN_LABEL, cell: (i) => formatLds(i.getValue()) }),
      col.accessor('method', { header: 'Method' }),
      col.accessor('status', {
        header: 'Status',
        cell: (i) => <Chip tone={statusTone(i.getValue())}>{i.getValue()}</Chip>,
      }),
      col.accessor('created_at', { header: 'Requested', cell: (i) => formatDateTime(i.getValue()) }),
    ],
    [],
  );

  return (
    <>
      <PageHeader
        title="Deposit"
        description={`Fund your activation wallet with ${TOKEN_LABEL} Token.`}
        tabs={WALLET_TABS}
      />

      <Card>
        <h3 className="card-title">Send {TOKEN_LABEL} to</h3>
        {details.data ? (
          <>
            <p className="deposit-address">{details.data.address}</p>
            <p className="text-muted small">
              Network: {details.data.chain} · Rate: {formatUsd(details.data.lds_rate_usd)} per {TOKEN_LABEL}
            </p>
            {details.data.qr_url ? <img src={details.data.qr_url} alt="Deposit QR code" width={180} /> : null}
          </>
        ) : null}
      </Card>

      <Card>
        <h3 className="card-title">Submit your transaction</h3>
        {submit.isSuccess ? <Alert tone="success">Deposit submitted for review.</Alert> : null}
        {submit.error ? <Alert tone="danger">{(submit.error as Error).message}</Alert> : null}

        <form onSubmit={form.handleSubmit((values) => submit.mutate(values))} noValidate>
          <FormGroup
            label="Amount (USD)"
            error={form.formState.errors.amount_usd?.message}
            hint={DEPOSIT_MAX_USD ? `Maximum $${DEPOSIT_MAX_USD} per deposit` : undefined}
          >
            <Input type="number" step="0.01" max={DEPOSIT_MAX_USD || undefined} {...form.register('amount_usd')} />
          </FormGroup>
          <FormGroup label="Transaction Hash" error={form.formState.errors.tx_hash?.message}>
            <Input placeholder="0x…" {...form.register('tx_hash')} />
          </FormGroup>
          <Button type="submit" variant="gold" disabled={submit.isPending}>
            {submit.isPending ? 'Submitting…' : 'Submit Deposit'}
          </Button>
        </form>
      </Card>

      <DataTable<DepositRecord>
        title="Deposit History"
        columns={columns}
        data={history.data?.data}
        meta={history.data?.meta}
        isLoading={history.isLoading}
        error={history.error as Error | null}
        onPageChange={setPage}
      />
    </>
  );
}
